import { motion } from "framer-motion";
import useApiData from "../hooks/useApiData";

const fallbackTransformationsPage = {
  hero: {
    badge: "Real Results",
    title: "Client Transformations",
    description:
      "See the results our athletes have achieved with DVX training programs",
  },
  transformation: [],
};

export default function Transformations() {
  const { data: pageData, loading } = useApiData(
    "/transformations",
    fallbackTransformationsPage,
  );

  // fix for key with space
  const items = pageData["transformation "] || [];

  return (
    <div className="min-h-screen bg-[var(--dvx-bg)] pt-24 transition-colors duration-300">
      {/* HERO */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest">
              {pageData.hero?.badge}
            </span>
            <h1 className="mt-4 font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              {pageData.hero?.title}
            </h1>
            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
              {pageData.hero?.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* TRANSFORMATIONS */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="font-oswald text-xl text-[var(--dvx-text)] uppercase tracking-widest animate-pulse">
                Loading...
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {items
                .filter((item) => item.is_active)
                .map((item, index) => {
                  const weightLost =
                    item.before_weight && item.after_weight
                      ? (item.before_weight - item.after_weight).toFixed(1)
                      : null;

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-[var(--dvx-surface)] border border-[var(--dvx-border)] overflow-hidden transition-colors duration-300"
                    >
                      {/* BEFORE / AFTER */}
                      <div className="relative h-80 md:h-96">
                        <div className="absolute inset-0 flex">
                          <div className="w-1/2 relative">
                            <img
                              src={item.before_image}
                              alt="Before"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <span className="absolute bottom-4 left-4 bg-black/70 text-white text-sm px-4 py-2 font-oswald uppercase">
                              Before
                            </span>
                          </div>

                          <div className="w-1/2 relative">
                            <img
                              src={item.after_image}
                              alt="After"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <span className="absolute bottom-4 right-4 bg-[var(--dvx-primary)] text-white text-sm px-4 py-2 font-oswald uppercase">
                              After
                            </span>
                          </div>
                        </div>

                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1 h-full bg-[var(--dvx-primary)]/50" />
                      </div>

                      {/* CONTENT */}
                      <div className="p-6">
                        <h3 className="font-oswald text-2xl font-bold text-[var(--dvx-text)] uppercase">
                          {item.client_name}
                        </h3>

                        <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
                          <span className="text-[var(--dvx-primary)] font-oswald uppercase">
                            {item.goal}
                          </span>

                          <span className="text-[var(--dvx-text-muted)]">
                            •
                          </span>

                          <span className="text-[var(--dvx-text-muted)]">
                            {item.duration_weeks} weeks
                          </span>

                          {weightLost && (
                            <>
                              <span className="text-[var(--dvx-text-muted)]">
                                •
                              </span>
                              <span className="text-green-500">
                                -{weightLost} kg
                              </span>
                            </>
                          )}
                        </div>

                        <p className="mt-4 text-[var(--dvx-text-muted)] italic">
                          "{item.description}"
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
